import type {
  ContractsListResponse,
  ContractsListResponseFor,
  ContractsQueryParams,
} from './types';

import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysContract } from '../queryKeys';

import { fetchContracts } from './fetchContracts';

/**
 * Generates the query options for the contracts query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching contracts.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractsQueryOptions = <
  TQueryParams extends Partial<ContractsQueryParams>,
  TData = ContractsListResponseFor<TQueryParams>,
>({
  queryParams,
  options,
}: {
  queryParams: TQueryParams;
  options?: Omit<
    UseQueryOptions<ContractsListResponseFor<TQueryParams>, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchContracts(queryParams, { signal }),
    queryKey: queryKeysContract.getAll(queryParams),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the data for all contracts with given query parameters.
 * Handles pagination and prefetches the next page of contracts for better performance.
 *
 * @param queryParams - The query parameters for fetching contracts.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/list_contracts_contracts_get
 */
export const useQueryContracts = (
  queryParams: Partial<ContractsQueryParams>,
  options?: Omit<
    UseQueryOptions<ContractsListResponse, Error, ContractsListResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  // const queryClient = useQueryClient();

  // // Prefetch the next page for better UX
  // useEffect(() => {
  //   const limit = queryParams.limit ?? 100;
  //   const offset = queryParams.offset ?? 0;

  //   void queryClient.prefetchQuery(
  //     getContractsQueryOptions({
  //       options,
  //       queryParams: {
  //         ...queryParams,
  //         offset: offset + limit,
  //       },
  //     }),
  //   );
  // }, [JSON.stringify(queryParams), JSON.stringify(options), queryClient]);

  return useQuery({
    ...getContractsQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
};
